import type {
  BulkSetRunItemStatusInput,
  CreateTestRunInput,
  ListTestRunsQuery,
  RerunInput,
  RunAutomationDto,
  RunAutomationPreviewDto,
  RunAutomationStatus,
  RunItemStatus,
  TestRunDto,
  TestRunListItemDto,
  TestRunResultDto,
  UpdateTestRunInput,
} from '@tms/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export type TestRunListParams = Partial<
  Pick<
    ListTestRunsQuery,
    'folderId' | 'status' | 'assigneeId' | 'checklistId' | 'q' | 'sort' | 'order'
  >
> & { page?: number; perPage?: number; includeSubfolders?: boolean };

export const testRunsQueryOptions = (projectCode: string, params: TestRunListParams) =>
  queryOptions({
    queryKey: queryKeys.testRuns.list(projectCode, params),
    queryFn: ({ signal }) =>
      api.list<TestRunListItemDto>(`/projects/${projectCode}/test-runs`, { ...params }, signal),
    placeholderData: (previous) => previous,
  });

export const testRunQueryOptions = (projectCode: string, runKey: string) =>
  queryOptions({
    queryKey: queryKeys.testRuns.detail(projectCode, runKey),
    queryFn: ({ signal }) =>
      api.get<TestRunDto>(`/projects/${projectCode}/test-runs/${runKey}`, undefined, signal),
  });

export const useTestRuns = (projectCode: string, params: TestRunListParams) =>
  useQuery(testRunsQueryOptions(projectCode, params));

export const useTestRun = (projectCode: string, runKey: string) =>
  useQuery(testRunQueryOptions(projectCode, runKey));

export const useRunItemResults = (projectCode: string, runId: string, itemId: string | null) =>
  useQuery({
    queryKey: queryKeys.testRuns.itemResults(runId, itemId ?? 'none'),
    queryFn: ({ signal }) =>
      api.get<TestRunResultDto[]>(
        `/projects/${projectCode}/test-runs/${runId}/items/${itemId}/results`,
        undefined,
        signal,
      ),
    enabled: itemId !== null,
  });

const useRunInvalidation = (projectCode: string) => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.testRuns.all(projectCode) });
    // A new or deleted run changes the counters beside the run folder tree.
    void queryClient.invalidateQueries({ queryKey: queryKeys.folders.tree(projectCode, 'run') });
    void queryClient.invalidateQueries({ queryKey: queryKeys.checklists.all(projectCode) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.overview(projectCode) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
  };
};

/**
 * Every execution mutation returns the whole run, so the detail cache is
 * written directly and the screen updates without a refetch round trip.
 */
const useRunMutation = <TInput>(
  projectCode: string,
  request: (input: TInput) => Promise<TestRunDto>,
) => {
  const queryClient = useQueryClient();
  const invalidate = useRunInvalidation(projectCode);

  return useMutation({
    mutationFn: request,
    onSuccess: (run) => {
      queryClient.setQueryData(queryKeys.testRuns.detail(projectCode, run.runKey), run);
      queryClient.setQueryData(queryKeys.testRuns.detail(projectCode, run.id), run);
      invalidate();
    },
  });
};

export const useCreateTestRun = (projectCode: string) =>
  useRunMutation(projectCode, (input: CreateTestRunInput) =>
    api.post<TestRunDto>(`/projects/${projectCode}/test-runs`, input),
  );

export const useUpdateTestRun = (projectCode: string, runId: string) =>
  useRunMutation(projectCode, (input: UpdateTestRunInput) =>
    api.patch<TestRunDto>(`/projects/${projectCode}/test-runs/${runId}`, input),
  );

export const useSetRunItemStatus = (projectCode: string, runId: string) =>
  useRunMutation(
    projectCode,
    ({
      itemId,
      status,
      comment,
    }: {
      itemId: string;
      status: RunItemStatus;
      comment?: string | null;
    }) =>
      api.patch<TestRunDto>(`/projects/${projectCode}/test-runs/${runId}/items/${itemId}`, {
        status,
        comment: comment ?? null,
      }),
  );

export const useBulkSetRunItemStatus = (projectCode: string, runId: string) =>
  useRunMutation(projectCode, (input: BulkSetRunItemStatusInput) =>
    api.post<TestRunDto>(`/projects/${projectCode}/test-runs/${runId}/items/bulk-status`, input),
  );

export const useCompleteTestRun = (projectCode: string, runId: string) =>
  useRunMutation(projectCode, () =>
    api.post<TestRunDto>(`/projects/${projectCode}/test-runs/${runId}/complete`),
  );

export const useReopenTestRun = (projectCode: string, runId: string) =>
  useRunMutation(projectCode, () =>
    api.post<TestRunDto>(`/projects/${projectCode}/test-runs/${runId}/reopen`),
  );

export const useRerunTestRun = (projectCode: string) =>
  useRunMutation(projectCode, ({ runId, input }: { runId: string; input: RerunInput }) =>
    api.post<TestRunDto>(`/projects/${projectCode}/test-runs/${runId}/rerun`, input),
  );

export const useDeleteTestRun = (projectCode: string) => {
  const invalidate = useRunInvalidation(projectCode);
  return useMutation({
    mutationFn: (runId: string) => api.delete(`/projects/${projectCode}/test-runs/${runId}`),
    onSuccess: invalidate,
  });
};

/* ─── autotests of a run ─── */

const automationKey = (projectCode: string, runId: string) =>
  [...queryKeys.testRuns.all(projectCode), 'automation', runId] as const;

const GOING: RunAutomationStatus[] = ['pending', 'running'];

/**
 * The run's linked autotests and their pipelines. Asked again every 15 s
 * while one goes — each answer also writes finished results into the run.
 */
export const useRunAutomation = (projectCode: string, runId: string) =>
  useQuery({
    queryKey: automationKey(projectCode, runId),
    queryFn: ({ signal }) =>
      api.get<RunAutomationDto>(
        `/projects/${projectCode}/test-runs/${runId}/automation`,
        undefined,
        signal,
      ),
    refetchInterval: (query) =>
      query.state.data?.launches.some((launch) => GOING.includes(launch.status)) ? 15_000 : false,
  });

/** Starts the linked autotests of the checks still not run. */
export const useStartRunAutomation = (projectCode: string, runId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<RunAutomationDto>(`/projects/${projectCode}/test-runs/${runId}/automation`),
    onSuccess: (automation) =>
      queryClient.setQueryData(automationKey(projectCode, runId), automation),
  });
};

/** Before a run is created: how many of the chosen checklists' checks have autotests. */
export const useRunAutomationPreview = (
  projectCode: string,
  checklistIds: string[],
  environmentId: string | null,
  enabled: boolean,
) =>
  useQuery({
    queryKey: [
      ...queryKeys.testRuns.all(projectCode),
      'automation-preview',
      checklistIds,
      environmentId,
    ],
    queryFn: ({ signal }) =>
      api.get<RunAutomationPreviewDto>(
        `/projects/${projectCode}/test-runs/automation-preview`,
        { checklistIds: checklistIds.join(','), ...(environmentId ? { environmentId } : {}) },
        signal,
      ),
    enabled: enabled && checklistIds.length > 0,
  });
